import { isKnownEnumValue, requireKnownEnumValue } from "@/lib/enum";
import { UpdateMcpServerFormVisibility as KnownUpdateMcpServerFormVisibility } from "@gram/client/models/components/updatemcpserverform.js";
import { RequireScope } from "@/components/require-scope";
import {
  FooterSaveButton,
  SettingsSection,
} from "@/components/detail/settings-section";
import { Button } from "@/components/ui/Button";
import { CopyButton } from "@/components/ui/CopyButton";
import { Dialog } from "@/components/ui/Dialog";
import { Field, FieldDescription, FieldLabel } from "@/components/ui/Field";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/Select";
import { Text } from "@/components/ui/Text";
import { Stack } from "@/components/ui/Stack";
import { useOrganization } from "@/contexts/Auth";
import {
  endpointUsesPrivateIngress,
  privateMcpEndpointUrls,
} from "@/hooks/usePrivateMcpServerUrls";
import { useNetworkIngressRollout } from "@/hooks/useNetworkIngressRollout";
import { useProductTier } from "@/hooks/useProductTier";
import { useOrgRoutes } from "@/routes";
import { customDomainMcpEndpointUrl } from "@/hooks/useToolsetUrl";
import type { Toolset } from "@/lib/toolTypes";

import { BOOK_DEMO_URL } from "@/lib/constants";
import { getServerURL } from "@/lib/utils";
import type { McpEndpoint } from "@gram/client/models/components/mcpendpoint.js";
import type { MetaMcpServer } from "@gram/client/models/components/metamcpserver.js";
import { invalidateAllGetMetaMcpServer } from "@gram/client/react-query/getMetaMcpServer.js";
import { invalidateAllMetaMcpServers } from "@gram/client/react-query/metaMcpServers.js";
import { useUpdateMetaMcpServerMutation } from "@gram/client/react-query/updateMetaMcpServer.js";
import {
  McpServerNetworkAccessMode,
  type McpServer,
} from "@gram/client/models/components/mcpserver.js";
import { UpdateMcpServerFormNetworkAccessMode } from "@gram/client/models/components/updatemcpserverform.js";
import { invalidateAllGetMcpServer } from "@gram/client/react-query/getMcpServer.js";
import { invalidateAllListToolsets } from "@gram/client/react-query/listToolsets.js";
import { invalidateAllToolset } from "@gram/client/react-query/toolset.js";
import { useNetworkIngress } from "@gram/client/react-query/networkIngress.js";
import { useListDomains } from "@gram/client/react-query/listDomains.js";
import { invalidateAllMcpServers } from "@gram/client/react-query/mcpServers.js";
import { useProductFeatures } from "@gram/client/react-query/productFeatures.js";
import { useUpdateMcpServerMutation } from "@gram/client/react-query/updateMcpServer.js";
import { useUpdateToolsetMutation } from "@gram/client/react-query/updateToolset.js";

import { useQueryClient } from "@tanstack/react-query";
import { Loader2 } from "lucide-react";
import { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { NetworkTrafficPanel } from "./NetworkTrafficPanel";

const NETWORK_ACCESS_LABELS: Record<
  UpdateMcpServerFormNetworkAccessMode,
  string
> = {
  public_only: "Public only",
  dual: "Public and private",
  private_only: "Private only",
};

type NetworkAccessProps =
  | {
      mcpServer: McpServer;
      metaMcpServer?: never;
      endpoints: McpEndpoint[];
      toolset?: never;
    }
  | {
      mcpServer?: never;
      metaMcpServer: MetaMcpServer;
      endpoints: McpEndpoint[];
      toolset?: never;
    }
  | {
      mcpServer?: never;
      metaMcpServer?: never;
      endpoints?: never;
      toolset: Toolset & {
        networkAccessMode?: McpServerNetworkAccessMode;
      };
    };

export function NetworkAccessSection({
  mcpServer,
  metaMcpServer,
  endpoints,
  toolset,
}: NetworkAccessProps): JSX.Element | null {
  const { status: rolloutStatus, canManageIngress } =
    useNetworkIngressRollout();
  const hasStoredPrivateMode =
    (mcpServer ?? metaMcpServer ?? toolset)?.networkAccessMode !== undefined &&
    (mcpServer ?? metaMcpServer ?? toolset)?.networkAccessMode !==
      McpServerNetworkAccessMode.PublicOnly;

  if (
    rolloutStatus === "disabled" &&
    !canManageIngress &&
    !hasStoredPrivateMode
  ) {
    return null;
  }

  const section = (
    <NetworkAccessSectionContent
      server={mcpServer ?? metaMcpServer}
      mcpServer={mcpServer}
      metaMcpServer={metaMcpServer}
      toolset={toolset}
      endpoints={endpoints ?? hostedToolsetEndpoint(toolset)}
      canReadIngress={canManageIngress}
    />
  );

  if (toolset) {
    return (
      <Stack gap={0} className="mb-8">
        {section}
      </Stack>
    );
  }

  return section;
}

function hostedToolsetEndpoint(
  toolset: (Toolset & { customDomainId?: string }) | undefined,
): McpEndpoint[] {
  if (!toolset?.mcpSlug) return [];
  return [
    {
      id: toolset.id,
      projectId: toolset.projectId,
      slug: toolset.mcpSlug,
      customDomainId: toolset.customDomainId,
      mcpServerId: toolset.id,
      isDomainRoot: false,
      createdAt: new Date(0),
      updatedAt: new Date(0),
    },
  ];
}

function NetworkAccessSectionContent({
  server,
  mcpServer,
  metaMcpServer,
  toolset,
  endpoints,
  canReadIngress,
}: {
  server?: McpServer | MetaMcpServer;
  mcpServer?: McpServer;
  metaMcpServer?: MetaMcpServer;
  toolset?: Toolset & {
    networkAccessMode?: McpServerNetworkAccessMode;
  };
  endpoints: McpEndpoint[];
  canReadIngress: boolean;
}): JSX.Element {
  const organization = useOrganization();
  const orgRoutes = useOrgRoutes();
  const enterprise = useProductTier() === "enterprise";
  const queryClient = useQueryClient();
  const currentMode =
    toolset?.networkAccessMode ??
    server?.networkAccessMode ??
    McpServerNetworkAccessMode.PublicOnly;
  const supportsEditing =
    isKnownEnumValue(UpdateMcpServerFormNetworkAccessMode, currentMode) &&
    (!mcpServer ||
      isKnownEnumValue(
        KnownUpdateMcpServerFormVisibility,
        mcpServer.visibility,
      ));
  const [draft, setDraft] = useState<McpServerNetworkAccessMode>(currentMode);
  const [confirmPrivateOnlyOpen, setConfirmPrivateOnlyOpen] = useState(false);

  useEffect(() => {
    setDraft(currentMode);
  }, [server?.id, toolset?.id, currentMode]);

  const features = useProductFeatures(
    { organizationId: organization.id },
    undefined,
    { throwOnError: false },
  );
  const ingressResult = useNetworkIngress(undefined, undefined, {
    enabled: canReadIngress,
    retry: false,
    throwOnError: false,
  });
  const ingress = ingressResult.data?.ingress;
  const hasCustomDomainEndpoints = endpoints.some(
    (endpoint) => endpoint.customDomainId,
  );
  const domainsResult = useListDomains(undefined, undefined, {
    refetchOnWindowFocus: false,
    retry: false,
    throwOnError: false,
    enabled: hasCustomDomainEndpoints,
  });
  const domains = domainsResult.data?.domains;

  const entitled = features.data?.networkIngressEnabled === true;
  const ingressOnline =
    ingress?.enabled === true && ingress.status === "online";
  const eligibleEndpoints = useMemo(
    () =>
      endpoints.filter(
        (endpoint) => ingress && endpointUsesPrivateIngress(endpoint, ingress),
      ),
    [endpoints, ingress],
  );
  const featureQuerySuccessful = features.isSuccess;
  const ingressQuerySuccessful = canReadIngress && ingressResult.isSuccess;
  const privateChoicesAvailable =
    featureQuerySuccessful &&
    entitled &&
    ingressQuerySuccessful &&
    ingressOnline &&
    eligibleEndpoints.length > 0;
  const privateStatusPending =
    features.isPending ||
    (featureQuerySuccessful &&
      entitled &&
      canReadIngress &&
      ingressResult.isPending);
  const privateStatusUnavailable =
    (!features.isPending && !featureQuerySuccessful) ||
    !canReadIngress ||
    (featureQuerySuccessful &&
      entitled &&
      canReadIngress &&
      !ingressResult.isPending &&
      !ingressQuerySuccessful);
  const customDomainUrlsResolved =
    !hasCustomDomainEndpoints ||
    (domainsResult.isSuccess &&
      !domainsResult.isFetching &&
      endpoints.every(
        (endpoint) =>
          !endpoint.customDomainId ||
          domains?.some((domain) => domain.id === endpoint.customDomainId) ===
            true,
      ));
  const privateOnlyAvailable =
    privateChoicesAvailable && customDomainUrlsResolved;

  const publicEndpointUrls = useMemo(() => {
    return Array.from(
      new Set(
        endpoints.flatMap((endpoint) => {
          if (!endpoint.customDomainId) {
            return [`${getServerURL()}/mcp/${endpoint.slug}`];
          }

          const domain = domains?.find(
            (candidate) => candidate.id === endpoint.customDomainId,
          );
          if (!domain) {
            return [];
          }

          const endpointUrl = customDomainMcpEndpointUrl(
            domain.domain,
            endpoint.slug,
          );
          return endpoint.isDomainRoot
            ? [`https://${domain.domain}/`, endpointUrl]
            : [endpointUrl];
        }),
      ),
    );
  }, [domains, endpoints]);
  const privateEndpointUrls = useMemo(
    () =>
      ingressQuerySuccessful && !ingressResult.isFetching
        ? privateMcpEndpointUrls(ingress, endpoints)
        : [],
    [endpoints, ingress, ingressQuerySuccessful, ingressResult.isFetching],
  );

  const onError = (error: Error) => {
    setConfirmPrivateOnlyOpen(false);
    toast.error(error.message || "Failed to update network access");
  };
  const update = useUpdateMcpServerMutation({
    onSuccess: async () => {
      setConfirmPrivateOnlyOpen(false);
      await Promise.all([
        invalidateAllGetMcpServer(queryClient, { refetchType: "all" }),
        invalidateAllMcpServers(queryClient, { refetchType: "all" }),
      ]);
      toast.success("Network access updated");
    },
    onError,
  });
  const updateHostedToolset = useUpdateToolsetMutation();
  const updateGateway = useUpdateMetaMcpServerMutation({
    onSuccess: async () => {
      setConfirmPrivateOnlyOpen(false);
      await Promise.all([
        invalidateAllGetMetaMcpServer(queryClient, { refetchType: "all" }),
        invalidateAllMetaMcpServers(queryClient, { refetchType: "all" }),
      ]);
      toast.success("Network access updated");
    },
    onError,
  });
  const isUpdating = toolset
    ? updateHostedToolset.isPending
    : mcpServer
      ? update.isPending
      : updateGateway.isPending;

  const save = () => {
    if (
      !supportsEditing ||
      !isKnownEnumValue(UpdateMcpServerFormNetworkAccessMode, draft)
    )
      return;
    if (
      (draft !== McpServerNetworkAccessMode.PublicOnly &&
        !privateChoicesAvailable) ||
      (draft === McpServerNetworkAccessMode.PrivateOnly &&
        !customDomainUrlsResolved)
    ) {
      return;
    }

    if (toolset) {
      updateHostedToolset.mutate(
        {
          request: {
            slug: toolset.slug,
            updateToolsetRequestBody: {
              networkAccessMode: requireKnownEnumValue(
                UpdateMcpServerFormNetworkAccessMode,
                draft,
              ),
            },
          },
        },
        {
          onSuccess: () => {
            setConfirmPrivateOnlyOpen(false);
            void Promise.all([
              invalidateAllToolset(queryClient, { refetchType: "all" }),
              invalidateAllListToolsets(queryClient, { refetchType: "all" }),
              invalidateAllGetMcpServer(queryClient, { refetchType: "all" }),
              invalidateAllMcpServers(queryClient, { refetchType: "all" }),
            ])
              .then(() => {
                toast.success("Network access updated");
              })
              .catch(onError);
          },
          onError,
        },
      );
    } else if (metaMcpServer) {
      updateGateway.mutate({
        request: {
          updateMetaMcpServerForm: {
            id: metaMcpServer.id,
            name: metaMcpServer.name,
            networkAccessMode: requireKnownEnumValue(
              UpdateMcpServerFormNetworkAccessMode,
              draft,
            ),
          },
        },
      });
    } else if (mcpServer) {
      update.mutate({
        request: {
          updateMcpServerForm: {
            id: mcpServer.id,
            networkAccessMode: requireKnownEnumValue(
              UpdateMcpServerFormNetworkAccessMode,
              draft,
            ),
            remoteMcpServerId: mcpServer.remoteMcpServerId ?? undefined,
            tunneledMcpServerId: mcpServer.tunneledMcpServerId ?? undefined,
            toolsetId: mcpServer.toolsetId ?? undefined,
            unproxiedMcpServerId: mcpServer.unproxiedMcpServerId ?? undefined,
            environmentId: mcpServer.environmentId ?? undefined,
            toolVariationsGroupId: mcpServer.toolVariationsGroupId ?? undefined,
            visibility: requireKnownEnumValue(
              KnownUpdateMcpServerFormVisibility,
              mcpServer.visibility,
            ),
          },
        },
      });
    }
  };

  const dirty = draft !== currentMode;
  const draftAllowed =
    draft === McpServerNetworkAccessMode.PublicOnly ||
    (draft === McpServerNetworkAccessMode.Dual
      ? privateChoicesAvailable
      : privateOnlyAvailable);
  const footerHint = networkAccessHint({
    entitled,
    ingressOnline,
    hasEligibleEndpoint: eligibleEndpoints.length > 0,
    endpointNamespaceKind: ingress?.endpointNamespaceKind,
    isGateway: !!metaMcpServer,
    privateStatusPending,
    privateStatusUnavailable,
    currentMode,
  });

  return (
    <SettingsSection>
      <SettingsSection.Header>
        <SettingsSection.Title>Network access</SettingsSection.Title>
        <SettingsSection.Description>
          Choose whether clients connect through public MCP routes, your private
          network ingress, or both.
        </SettingsSection.Description>
      </SettingsSection.Header>
      <SettingsSection.Panel>
        <SettingsSection.Body>
          {!supportsEditing && (
            <Text small muted>
              Refresh the page before editing this unsupported network
              configuration.
            </Text>
          )}
          <Field className="max-w-md">
            <FieldLabel htmlFor="mcp-network-access-mode">
              Access mode
            </FieldLabel>
            <Select
              value={draft}
              onValueChange={(value) =>
                setDraft(value as UpdateMcpServerFormNetworkAccessMode)
              }
              disabled={isUpdating || !supportsEditing}
            >
              <SelectTrigger
                id="mcp-network-access-mode"
                className="w-full"
                aria-label="Network access mode"
              >
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem
                  value={McpServerNetworkAccessMode.PublicOnly}
                  description="Serve this MCP server through public routes only"
                >
                  {NETWORK_ACCESS_LABELS.public_only}
                </SelectItem>
                <SelectItem
                  value={McpServerNetworkAccessMode.Dual}
                  disabled={!privateChoicesAvailable}
                  description="Keep public routes and also serve through private ingress"
                >
                  {NETWORK_ACCESS_LABELS.dual}
                </SelectItem>
                <SelectItem
                  value={McpServerNetworkAccessMode.PrivateOnly}
                  disabled={!privateOnlyAvailable}
                  description="Stop public routes and serve only through private ingress"
                >
                  {NETWORK_ACCESS_LABELS.private_only}
                </SelectItem>
              </SelectContent>
            </Select>
            <FieldDescription>
              Private modes are available only while the organization is
              entitled and its private ingress is online.
            </FieldDescription>
          </Field>
          {canReadIngress && (
            <Button asChild variant="secondary" size="sm">
              <orgRoutes.domains.Link>
                Configure organization network access
              </orgRoutes.domains.Link>
            </Button>
          )}
          {currentMode !== McpServerNetworkAccessMode.PublicOnly &&
            privateEndpointUrls.length > 0 && (
              <Field>
                <FieldLabel>Private endpoint URLs</FieldLabel>
                <ul className="space-y-1">
                  {privateEndpointUrls.map((url) => (
                    <li key={url} className="flex items-start gap-1">
                      <span className="min-w-0 flex-1 break-all font-mono text-sm">
                        {url}
                      </span>
                      <CopyButton
                        text={url}
                        size="xs"
                        tooltip="Copy private endpoint URL"
                      />
                    </li>
                  ))}
                </ul>
                <FieldDescription>
                  Use these addresses from devices connected to the
                  organization&apos;s tailnet.
                </FieldDescription>
              </Field>
            )}
          {currentMode !== McpServerNetworkAccessMode.PublicOnly && (
            <NetworkTrafficPanel
              mcpServerId={mcpServer?.id ?? toolset?.id}
              metaMcpServerId={metaMcpServer?.id}
            />
          )}
        </SettingsSection.Body>
        <SettingsSection.Footer>
          <SettingsSection.FooterHint>
            {!enterprise &&
            featureQuerySuccessful &&
            !entitled &&
            currentMode === McpServerNetworkAccessMode.PublicOnly ? (
              <>
                Tailscale private access is available on the Enterprise plan.{" "}
                <a
                  href={BOOK_DEMO_URL}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="underline"
                >
                  Talk to our team about upgrading
                </a>
              </>
            ) : (
              footerHint
            )}
          </SettingsSection.FooterHint>
          <SettingsSection.FooterActions>
            <RequireScope
              scope="mcp:write"
              resourceId={toolset?.projectId ?? server?.projectId}
              level="component"
            >
              <FooterSaveButton
                pending={isUpdating}
                disabled={
                  !dirty || !draftAllowed || isUpdating || !supportsEditing
                }
                onClick={() => {
                  if (draft === McpServerNetworkAccessMode.PrivateOnly) {
                    setConfirmPrivateOnlyOpen(true);
                    return;
                  }
                  save();
                }}
              />
            </RequireScope>
          </SettingsSection.FooterActions>
        </SettingsSection.Footer>
      </SettingsSection.Panel>

      <Dialog
        open={confirmPrivateOnlyOpen}
        onOpenChange={setConfirmPrivateOnlyOpen}
      >
        <Dialog.Content className="max-w-lg">
          <Dialog.Header>
            <Dialog.Title>
              Make this {metaMcpServer ? "gateway" : "server"} private only?
            </Dialog.Title>
            <Dialog.Description>
              This changes how every client reaches this{" "}
              {metaMcpServer ? "gateway" : "MCP server"}.
            </Dialog.Description>
          </Dialog.Header>
          <ul className="text-muted-foreground list-disc space-y-2 pl-5 text-sm">
            <li>
              Public routes stop serving this{" "}
              {metaMcpServer ? "gateway" : "MCP server"}. There is no public
              fallback.
            </li>
            <li>
              If the tailnet or private ingress loses connectivity, this MCP
              {metaMcpServer ? "gateway" : "server"} is unavailable until
              service returns or you switch back to public access.
            </li>
            <li>
              {metaMcpServer
                ? "Existing client URLs"
                : "Marketplace and device-agent URLs"}{" "}
              are not rewritten to the private address. Update those clients
              separately.
            </li>
          </ul>
          {publicEndpointUrls.length > 0 && (
            <div className="space-y-2">
              <Text small>Public endpoint URLs that will stop serving:</Text>
              <ul className="text-muted-foreground list-disc space-y-1 pl-5 text-sm">
                {publicEndpointUrls.map((url) => (
                  <li key={url} className="break-all font-mono text-xs">
                    {url}
                  </li>
                ))}
              </ul>
            </div>
          )}
          {privateEndpointUrls.length > 0 && (
            <div className="space-y-2">
              <Text small>Private endpoint URLs that remain available:</Text>
              <ul className="text-muted-foreground list-disc space-y-1 pl-5 text-sm">
                {privateEndpointUrls.map((url) => (
                  <li key={url} className="break-all font-mono text-xs">
                    {url}
                  </li>
                ))}
              </ul>
            </div>
          )}
          <Dialog.Footer>
            <Button
              variant="secondary"
              disabled={isUpdating || !supportsEditing}
              onClick={() => setConfirmPrivateOnlyOpen(false)}
            >
              <Button.Text>Cancel</Button.Text>
            </Button>
            <Button
              variant="destructive-primary"
              disabled={isUpdating || !privateOnlyAvailable}
              onClick={save}
            >
              {isUpdating && (
                <Button.LeftIcon>
                  <Loader2 aria-hidden="true" className="size-4 animate-spin" />
                </Button.LeftIcon>
              )}
              <Button.Text>Make private only</Button.Text>
            </Button>
          </Dialog.Footer>
        </Dialog.Content>
      </Dialog>
    </SettingsSection>
  );
}

function networkAccessHint({
  entitled,
  ingressOnline,
  hasEligibleEndpoint,
  endpointNamespaceKind,
  isGateway,
  privateStatusPending,
  privateStatusUnavailable,
  currentMode,
}: {
  entitled: boolean;
  ingressOnline: boolean;
  hasEligibleEndpoint: boolean;
  endpointNamespaceKind?: string;
  isGateway: boolean;
  privateStatusPending: boolean;
  privateStatusUnavailable: boolean;
  currentMode:
    | McpServer["networkAccessMode"]
    | MetaMcpServer["networkAccessMode"];
}): string {
  if (privateStatusPending) {
    return "Checking private network availability…";
  }
  if (privateStatusUnavailable) {
    return currentMode === McpServerNetworkAccessMode.PublicOnly
      ? "Private network availability could not be checked. An organization admin can verify the ingress."
      : "Private network availability could not be checked. You can still switch to public only.";
  }
  if (!entitled) {
    return currentMode === McpServerNetworkAccessMode.PublicOnly
      ? "Private network access is not enabled for this organization. Contact support to enable it."
      : "Private network access is no longer enabled. You can still switch to public only.";
  }
  if (!ingressOnline) {
    return currentMode === McpServerNetworkAccessMode.PublicOnly
      ? "Bring the organization's private ingress online to enable private access."
      : "Private ingress is offline. You can still switch to public only.";
  }
  if (!hasEligibleEndpoint) {
    const address =
      endpointNamespaceKind === "custom_domain"
        ? "Custom Address on the domain configured for your private network"
        : "Hosted Address";
    const section = isGateway ? "Gateway URL" : "Server URL";
    return currentMode === McpServerNetworkAccessMode.PublicOnly
      ? `In ${section}, add a ${address} so clients can reach this ${isGateway ? "gateway" : "server"} through your private network.`
      : `This ${isGateway ? "gateway" : "server"} no longer has a ${address} in ${section}, so clients cannot reach it through your private network. You can still switch to public only.`;
  }
  return "Changes apply to new connections.";
}
